import { Check, Close, ContentCopy, MoreVert } from '@mui/icons-material';
import { Button, Menu, MenuItem, TextField } from '@mui/material';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useAlertsStore, useUserStore } from '@/configs/store/useRootStore';
import { useYaMetrika } from '@/entities/yaMetrika';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { useIsLogsReadOnly } from '@/features/logsMode';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { colorStore } from '@/features/queueColors';
import { ReadonlyGuard } from '@/features/ReadonlyGuard';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { WeeklyWorkLog } from '@/types';
import { MuiColors } from '@/types/colors';
import { UserRole } from '@/types/user';
import { AnalyticsEvent } from '@/types/yametrika';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { getTrackerUrl } from '@/utils/getTrackerUrl';
import { parseTimeExpression } from '@/utils/parseTimeInput';

import { useLogCardMenuItems } from '../useLogCardMenuItems';

import s from './LogCard.module.scss';

type FormValues = {
  duration: string;
};

type LogCardProps = {
  log: WeeklyWorkLog;
  onOpenDetails: (log: WeeklyWorkLog) => void;
  readOnly?: boolean;
};

const LogCard: React.FC<LogCardProps> = ({ log, onOpenDetails, readOnly }) => {
  const alertsStore = useAlertsStore();
  const { sendEvent } = useYaMetrika();
  const { user: currentUser } = useUserStore();
  const { isViewingSelf } = useGlobalUserFilterModel();
  const isLogsReadOnly = useIsLogsReadOnly();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editTime, setEditTime] = useState<boolean>(false);

  const keyPrefix = log.taskKey.split('-')[0];
  const color = colorStore.getColorByPrefix(keyPrefix);
  const needsBorder = colorStore.needsBorder(keyPrefix);
  const duration = useMemo(() => formatDurationRussian(log.durationMinutes), [log.durationMinutes]);

  const { logOperations, disabledLogId, lastAddId } = useTimeLogStore();

  const handleDelete = () => modalManagerStore.open('deleteLog', log);
  const handleEdit = () => modalManagerStore.open('editLog', log);
  const handleCopy = () => modalManagerStore.open('copyLog', { log });

  const menuItems = useLogCardMenuItems({ log, handleDelete, handleEdit, handleCopy });
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      duration: duration,
    },
  });

  const watchedDuration = watch('duration');

  useEffect(() => {
    reset({ duration });
  }, [duration, reset]);

  const isDurationValid = useMemo(() => {
    if (!watchedDuration || !watchedDuration.trim()) return false;

    return Boolean(parseTimeExpression(watchedDuration));
  }, [watchedDuration]);

  const isAdmin = currentUser?.roles.includes(UserRole.admin);
  const canEdit = (isViewingSelf || isAdmin) && !isLogsReadOnly;

  const open = Boolean(anchorEl);

  const handleCardClick = () => {
    onOpenDetails(log);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClose = () => {
    setEditTime(false);
    reset();
  };

  const handleCopyLink = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const response = await copyToClipboard(getTrackerUrl(log.taskKey));

    if (!response.isError) {
      alertsStore.addAlert({
        title: 'Ссылка успешно скопирована!',
        message: '',
        withReload: false,
        autoClose: true,
      });

      sendEvent(AnalyticsEvent.copyTaskName);
    }
  };

  const handleUpdateLog = async (data: FormValues) => {
    if (parseTimeExpression(data.duration) === log.durationMinutes) {
      setEditTime(false);

      return;
    }

    const comment = log.comment ?? '';
    const dataDuration = parseTimeExpression(data.duration);

    if (dataDuration) {
      await logOperations.updateLog({
        workLogId: log.id,
        taskKey: log.taskKey,
        duration: dataDuration,
        comment,
      });
      setEditTime(false);
    }
  };

  const handleTaskLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    sendEvent(AnalyticsEvent.goToTask);
  };

  return (
    <>
      <div
        className={clsx(s.card, {
          [s.disabled]: disabledLogId === log.id,
          [s.highlight]: lastAddId === log.id,
        })}
        onClick={() => {
          if (!editTime) handleCardClick();
        }}
      >
        <div
          className={s.card__color}
          style={{
            backgroundColor: color,
            ...(needsBorder && {
              border: '1px solid rgba(0, 0, 0, 0.2)',
              borderLeft: 'none',
            }),
          }}
        />
        <div className={s.card__wrapper}>
          <div className={s.card__header}>
            <h2>
              <a
                onClick={handleTaskLinkClick}
                href={getTrackerUrl(log.taskKey)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color,
                  ...(needsBorder && {
                    textShadow: '0 0 1px rgba(0, 0, 0, 0.5), 0 0 1px rgba(0, 0, 0, 0.5)',
                  }),
                }}
              >
                {log.taskKey}
              </a>
              <Button
                onClick={handleCopyLink}
                sx={{ padding: 0, minWidth: '5px', marginLeft: '5px', marginBottom: '3px' }}
              >
                <ContentCopy
                  sx={{ width: '15px', height: '15px', color: MuiColors.secondaryColor }}
                />
              </Button>
            </h2>
            {!readOnly && (
              <div className={s.card__header_more}>
                <ReadonlyGuard>
                  <Button
                    sx={{
                      padding: 0,
                      minWidth: '5px',
                      borderRadius: '6px',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick(e);
                    }}
                  >
                    <MoreVert sx={{ width: '18px', height: '18px', color }} />
                  </Button>
                </ReadonlyGuard>
              </div>
            )}
          </div>
          <div className={s.card__body}>
            <p className={s['card__body-title']}>{log.taskTitle} </p>
            {log.comment && (
              <div className={s['card__body-description']}>
                <pre>{log.comment}</pre>
              </div>
            )}
            <div className={s.card__time}>
              {readOnly ? (
                <span style={{ color: MuiColors.secondaryColor, fontSize: 'inherit' }}>
                  {duration}
                </span>
              ) : !editTime ? (
                <Button
                  sx={{
                    padding: '2px 6px',
                    minWidth: '5px',
                    borderRadius: '6px',
                    color: MuiColors.secondaryColor,
                    transition: 'background-color 0.15s, color 0.15s',
                    '&:hover': {
                      backgroundColor: MuiColors.veryLightAccentColor,
                      color: MuiColors.accentColor,
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (canEdit) {
                      setEditTime(true);
                    }
                  }}
                >
                  {duration}
                </Button>
              ) : (
                <React.Fragment>
                  <Button
                    disabled={!isDurationValid}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubmit(handleUpdateLog)();
                    }}
                    sx={{ minWidth: '14px', height: '14px', width: '14px' }}
                  >
                    <Check sx={{ width: '14px', height: '14px' }} />
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClose();
                    }}
                    sx={{ minWidth: '14px', height: '14px', width: '14px' }}
                  >
                    <Close sx={{ width: '14px', height: '14px', color: MuiColors.dangerColor }} />
                  </Button>
                  <Controller
                    control={control}
                    name="duration"
                    rules={{ required: 'Введите время' }}
                    render={({ field }) => {
                      const parsedDuration = parseTimeExpression(field.value);
                      const isParsed = Boolean(parsedDuration);

                      return (
                        <>
                          <TextField
                            {...field}
                            size="small"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSubmit(handleUpdateLog)();
                              }

                              if (e.key === 'Escape') {
                                e.preventDefault();
                                handleEditClose();
                              }
                            }}
                            sx={{
                              height: '21px',
                              fontSize: '12px',
                              '& .MuiInputBase-input': {
                                width: '45px',
                                height: '21px',
                                fontSize: '12px',
                                padding: '0 3px',
                                '@media (max-width:1500px)': {
                                  fontSize: '10px',
                                },
                              },
                              '@media (max-width:1500px)': {
                                fontSize: '10px',
                              },
                            }}
                          />
                          <div
                            className={clsx(s.card__parsed_time, {
                              [s.correct]: isParsed,
                              [s.incorrect]: !isParsed,
                            })}
                          >
                            <span>
                              {field.value
                                ? isParsed
                                  ? formatDurationRussian(parsedDuration)
                                  : '0ч'
                                : '0ч'}
                            </span>
                          </div>
                        </>
                      );
                    }}
                  />
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
        {!readOnly && (
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleClose();
            }}
            sx={{
              '.MuiPaper-root': {
                boxShadow: '0px 0px 10px 2px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              },
              '.MuiMenuItem-root': {
                fontSize: '14px',
                gap: '10px',
              },
            }}
          >
            {menuItems.map(({ label, icon, action, danger }) => (
              <MenuItem
                key={label}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                  action();
                }}
                sx={danger ? { color: MuiColors.dangerColor } : { color: MuiColors.primaryColor }}
              >
                {icon}
                <p>{label}</p>
              </MenuItem>
            ))}
          </Menu>
        )}
      </div>
    </>
  );
};

export default observer(LogCard);
